import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { KeyRound } from "lucide-react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Button, ErrorText, Input, Label, Spinner } from "@/components/ui";
import { api } from "@/lib/api";
import { canAutoFocus } from "@/lib/viewport";
import { useMe, useSetMe } from "@/lib/auth";
import { APP_NAME } from "@/lib/brand";
import type { AuthStatus, User } from "@/lib/types";

export default function Login() {
  const me = useMe();
  const setMe = useSetMe();
  const status = useQuery({ queryKey: ["auth-status"], queryFn: () => api.get<AuthStatus>("/api/auth/status") });
  const [mode, setMode] = useState<"login" | "register">("login");
  const [form, setForm] = useState({ username: "", name: "", email: "", password: "", setupToken: "" });
  const [params] = useSearchParams();
  const [error, setError] = useState<unknown>(params.get("error"));
  const [busy, setBusy] = useState(false);

  if (me.data) return <Navigate to="/" replace />;
  if (status.isLoading || me.isLoading)
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size={24} />
      </div>
    );

  const setup = status.data?.needs_setup ?? false;
  const registering = setup || mode === "register";

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const user = registering
        ? await api.post<User>("/api/auth/register", {
            username: form.username,
            name: form.name || form.username,
            email: form.email || null,
            password: form.password,
            setup_token: setup ? form.setupToken || null : null,
          })
        : await api.post<User>("/api/auth/login", { username: form.username, password: form.password });
      setMe(user);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });

  return (
    <div className="flex h-full items-center justify-center overflow-y-auto bg-zinc-50 p-4 dark:bg-zinc-950">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
        <div>
          <h1 className="text-xl font-semibold">{setup ? `Welcome to ${APP_NAME}` : registering ? "Create account" : "Sign in"}</h1>
          {setup && (
            <p className="mt-1 text-sm text-zinc-500">
              {status.data?.oidc_enabled
                ? `The first account becomes the administrator: sign in with ${status.data.oidc_name}, or create one with a password.`
                : "Create the first account. It will be the administrator."}
            </p>
          )}
        </div>
        {/* Also on an empty instance: the first single sign-on login becomes the admin (who gets
            that far is decided by the identity provider). */}
        {status.data?.oidc_enabled && (
          <>
            <a
              href="/api/auth/oidc/login"
              className="flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-zinc-300 bg-white text-sm font-medium hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:bg-zinc-800"
            >
              <KeyRound size={16} /> Sign in with {status.data.oidc_name}
            </a>
            {status.data.password_login && (
              <div className="flex items-center gap-3 text-xs text-zinc-500">
                <span className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800" /> or <span className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800" />
              </div>
            )}
          </>
        )}
        {!status.data?.password_login && <ErrorText error={error} />}
        {status.data?.password_login !== false && (
        <>
        <Label label={registering ? "Username" : "Username or email"}>
          <Input autoFocus={canAutoFocus()} autoComplete="username" value={form.username} onChange={set("username")} required />
        </Label>
        {setup && status.data?.setup_token_required && (
          <Label label="Setup code" hint="From the server's WEBTHING_SETUP_TOKEN setting.">
            <Input value={form.setupToken} onChange={set("setupToken")} autoComplete="off" required />
          </Label>
        )}
        {registering && (
          <>
            <Label label="Display name">
              <Input value={form.name} onChange={set("name")} placeholder={form.username} />
            </Label>
            <Label label="Email" hint="Optional. Used for notifications.">
              <Input type="email" value={form.email} onChange={set("email")} />
            </Label>
          </>
        )}
        <Label label="Password" hint={registering ? "At least 8 characters." : undefined}>
          <Input
            type="password"
            autoComplete={registering ? "new-password" : "current-password"}
            value={form.password}
            onChange={set("password")}
            minLength={registering ? 8 : undefined}
            required
          />
        </Label>
        <ErrorText error={error} />
        <Button className="w-full" disabled={busy}>
          {busy && <Spinner />}
          {registering ? "Create account" : "Sign in"}
        </Button>
        {!setup && status.data?.open_registration && (
          <button
            type="button"
            className="w-full text-center text-sm text-indigo-600 hover:underline dark:text-indigo-400"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
          >
            {mode === "login" ? "Create an account" : "I already have an account"}
          </button>
        )}
        </>
        )}
      </form>
    </div>
  );
}
